/**
 * lineageParse.js — JS mirror of the backend `_split_lineage_text` helper.
 *
 * Splits a free-text lineage string into exactly two parent fragments when
 * the text matches a clear binary cross pattern. Used by:
 *   - LineageSubmitModal (live preview + "Use detected parents" hint)
 *
 * Backend equivalents (must stay in sync):
 *   - server.py :: _split_lineage_text
 *   - server.py :: _clean_parent_part
 *
 * Supported separators (priority order):
 *   "  × "  (unicode multiplication with whitespace)
 *   "×"     (unicode multiplication, no whitespace)
 *   " x "   (lowercase x with whitespace)
 *   " X "   (uppercase X with whitespace)
 *   " / "   (slash with whitespace)
 *
 * Returns [parent_one, parent_two] when a clean binary split is found,
 * or [] otherwise (empty, single token, 3+ way crosses, numeric-only
 * fragments).
 */

const SEPARATOR_PATTERNS = [
  /\s+×\s+/,
  /\s*×\s*/,
  /\s+x\s+/,
  /\s+X\s+/,
  /\s+\/\s+/,
];

function cleanParentPart(p) {
  if (!p) return "";
  let s = String(p).trim();
  // Strip a single surrounding pair of parentheses ONLY when they truly
  // wrap the entire fragment (i.e., the closing paren is the very last
  // character and matches the opening one without intermediate closure).
  s = stripBalancedOuterParens(s);
  // Drop any trailing parenthesized note, e.g. "Foo (F4)" -> "Foo"
  s = s.replace(/\s*\([^)]*\)\s*$/, "").trim();
  return s.slice(0, 160);
}

function stripBalancedOuterParens(s) {
  if (!s || s[0] !== "(" || s[s.length - 1] !== ")") return s;
  let depth = 0;
  for (let i = 0; i < s.length; i++) {
    if (s[i] === "(") depth++;
    else if (s[i] === ")") {
      depth--;
      // First "(" closes before the end → outer parens are NOT a wrapper.
      if (depth === 0 && i < s.length - 1) return s;
    }
  }
  return s.slice(1, -1).trim();
}

export function splitLineageText(text) {
  if (!text || typeof text !== "string") return [];
  let s = text.trim();
  if (!s) return [];
  // Strip a single balanced pair of surrounding parentheses.
  s = stripBalancedOuterParens(s);
  for (const sep of SEPARATOR_PATTERNS) {
    const parts = s.split(sep);
    if (parts.length === 2) {
      const cleaned = parts.map(cleanParentPart);
      if (cleaned.every((p) => p) && !cleaned.some((p) => /^[\d\s,]+$/.test(p))) {
        return cleaned;
      }
    }
  }
  return [];
}
